import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  Chip,
  Tooltip,
  Button,
} from "@mui/material";
import EmptyState from "./EmptyState";
import PeopleIcon from "@mui/icons-material/People";

function UserTable({
  users,
  onUserClick,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onCollectorApproval,
  onAccountStatusManage,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  // Reset page when users change
  React.useEffect(() => {
    setPage(0);
  }, [users]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sorted = useMemo(() => {
    const arr = [...users];
    arr.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (orderBy === "createdAt" || orderBy === "dreamLastRequestAt") {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      if (order === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return arr;
  }, [users, orderBy, order]);

  const paged = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const clickable = Boolean(onUserClick);
  const hasDreamActivityColumns = users.some(
    (user) => user.dreamRequestCount != null || user.dreamLastRequestAt
  );

  const handleSelectAll = (e) => {
    if (!onSelectionChange) return;
    if (e.target.checked) {
      onSelectionChange(users.map((u) => u.userId));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelect = (userId) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(userId)) {
      onSelectionChange(selectedIds.filter((id) => id !== userId));
    } else {
      onSelectionChange([...selectedIds, userId]);
    }
  };

  if (!users || users.length === 0) {
    return <EmptyState message="표시할 사용자가 없습니다." icon={PeopleIcon} />;
  }

  const getSocialChip = (user) => {
    if (user.socialProvider === "kakao") {
      return (
        <Chip
          label="카카오"
          size="small"
          sx={{ bgcolor: "#FEE500", color: "#000", ml: 0.5 }}
        />
      );
    }
    if (user.socialProvider === "naver") {
      return (
        <Chip
          label="네이버"
          size="small"
          sx={{ bgcolor: "#03C75A", color: "#fff", ml: 0.5 }}
        />
      );
    }
    return null;
  };

  return (
    <Paper sx={{ borderRadius: 3 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < users.length
                    }
                    checked={
                      selectedIds.length === users.length && users.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleSort("name")}
                >
                  이름
                </TableSortLabel>
              </TableCell>
              <TableCell>이메일</TableCell>
              <TableCell>전화번호</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>수거 장소</TableCell>
              {hasDreamActivityColumns && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "dreamRequestCount"}
                    direction={orderBy === "dreamRequestCount" ? order : "asc"}
                    onClick={() => handleSort("dreamRequestCount")}
                  >
                    요청/작업 수
                  </TableSortLabel>
                </TableCell>
              )}
              {hasDreamActivityColumns && (
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "dreamLastRequestAt"}
                    direction={orderBy === "dreamLastRequestAt" ? order : "asc"}
                    onClick={() => handleSort("dreamLastRequestAt")}
                  >
                    최근 요청
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleSort("createdAt")}
                >
                  가입일
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((user) => (
              <TableRow
                key={user.userId}
                hover
                sx={{ cursor: clickable ? "pointer" : "default" }}
                onClick={() => onUserClick?.(user.userId)}
              >
                {selectable && (
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedIds.includes(user.userId)}
                      onChange={() => handleSelect(user.userId)}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 500 }}>
                  {user.name}
                  {getSocialChip(user)}
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.phoneNumber || "-"}</TableCell>
              <TableCell>
                {user.accountStatus ? (
                  <Chip
                    size="small"
                    label={user.accountStatus}
                    color={user.accountStatus === "APPROVED" ? "success" : user.accountStatus === "PENDING_APPROVAL" ? "warning" : "default"}
                  />
                ) : "-"}
                {onCollectorApproval && (
                  <Button
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCollectorApproval(user);
                    }}
                  >
                    승인관리
                  </Button>
                )}
                {!onCollectorApproval && onAccountStatusManage && (
                  <Button
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAccountStatusManage(user);
                    }}
                  >
                    상태관리
                  </Button>
                )}
              </TableCell>
              <TableCell>
                  {user.locations?.length ? (
                    <Tooltip
                      title={user.locations
                        .map((l) => l.locationName)
                        .join(", ")}
                    >
                      <Chip
                        label={`${user.locations.length}곳`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                {hasDreamActivityColumns && (
                  <TableCell>{user.dreamRequestCount ?? 0}건</TableCell>
                )}
                {hasDreamActivityColumns && (
                  <TableCell>
                    {user.dreamLastRequestAt
                      ? new Date(user.dreamLastRequestAt).toLocaleDateString()
                      : "-"}
                  </TableCell>
                )}
                <TableCell>
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={users.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="페이지당 행"
      />
    </Paper>
  );
}

export default UserTable;
